import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import StepContainer from "./StepContainer";
import Product from "../../../../api/entities/Product";
import { OnboardingContext } from "../../../../store/OnboardingContext";
import { createPayment, getProducts } from "../../../../api/client";
import {
  MEMBER_STATUS_STANDARD,
  MEMBER_STATUS_TRIAL,
} from "../../../../api/entities/UserProfile";
import { Box, Button, Grid, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { AccountContext } from "../../../../store/AccountContext";
import BuyNowPurchase from "../../../Payment/Steps";
import useLogger from "../../../../hooks/useLogger";
import { isBrowser } from "react-device-detect";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `sx`.
 * See docs/mui-conversion-guide.md.
 *
 * **PAYMENTS / SUBSCRIPTIONS ESCALATION CLASS.** This step starts a free
 * trial (`createPayment` on the trial product) or opens the Stripe plan
 * chooser. Styling-only conversion: `takeTrial`, `buyNow`, the product fetch,
 * the `logMetricEvent` free-trial beacon and `validateLogin()` were left
 * byte-for-byte unchanged by it, as was every rendering condition (#1225 later
 * changed the trial's event ID in `takeTrial`).
 *
 * DEAD RULES DROPPED (guide §1.2 row 1). The v4 `useStyles` object declared
 * ~90 keys, of which exactly FIVE were ever referenced from this file's JSX:
 * `infoBox`, `subHeading`, `bodyText`, `tryBuyContainer` and `primaryButton`
 * (verified by grepping `classes.` before deleting). Everything else — the
 * countdown, player, share, notify-me, credits, tags, channel and bottom-band
 * rules, left over from an earlier version of this page — emitted CSS that no
 * element in this component ever carried, so it rendered nothing. The v4
 * definitions remain in git history if the markup they belonged to ever
 * returns.
 */

// was: useStyles().infoBox
const infoBoxSx: SxProps<Theme> = (theme) => ({
  textAlign: "center",
  marginTop: theme.spacing(3),
});

// was: useStyles().subHeading.
// Its first declaration was `color: theme.palette.background.white` — there is
// no `white` key on the palette's `background` (see theme/tokens.ts), so the
// value was `undefined` and JSS dropped the declaration: this heading has never
// had a colour rule of its own. Not carried across, for the same reason an
// empty rule isn't (guide §1.2 row 1) — reinstating it as some literal white
// would be a visual change, not a port.
const subHeadingSx: SxProps<Theme> = (theme) => ({
  fontSize: "1.6rem",
  lineHeight: "1.6rem",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
    lineHeight: "2.2rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx: SxProps<Theme> = {
  color: "common.white",
  textAlign: "center",
};

/**
 * was: useStyles().tryBuyContainer.
 *
 * The two `<Grid item>`s carrying it also carried one alignment prop each —
 * `alignItems="center"` on the trial button, `justify="center"` on the buy
 * button. Neither prop exists on v9's Grid (it would be forwarded to the DOM
 * as an unknown attribute — guide §2), and this element genuinely IS a flex
 * container because the class sets `display: flex`, so both really rendered in
 * v4 and are kept. They stay at the call sites rather than being folded in
 * here, so each element gets exactly the declaration it had: `justifyContent`
 * was already a no-op duplicate of the class rule, and `alignItems` applied to
 * only one of the two.
 */
const tryBuyContainerSx = {
  // padding: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
};

// v9's `Grid` types its `sx` as a single `SystemStyleObject`, not `SxProps`, so
// the per-element alignment cannot be layered with the `sx={[a, b]}` array form.
const tryBuyContainerAlignItemsSx: SxProps<Theme> = {
  ...tryBuyContainerSx,
  alignItems: "center",
};
const tryBuyContainerJustifySx: SxProps<Theme> = {
  ...tryBuyContainerSx,
  justifyContent: "center",
};

/**
 * was: useStyles().primaryButton, plus a v4 default that has to be restored.
 *
 * PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"`
 * is NOT in the v4 rule. Both Buttons omit the `color` prop, so v4 rendered
 * them `color="default"` → `color: theme.palette.text.primary`. v5+ deleted
 * `default` and made `primary` the default — #99ca3c — which is exactly the
 * `backgroundColor` this rule paints on, i.e. an invisible "Start Free Trial"
 * / "View Membership Plans" button at the end of signup.
 */
const primaryButtonSx: SxProps<Theme> = {
  backgroundColor: "background.ffGreen",
  width: "80%",
  margin: "12px 5%",
  fontSize: "1.1rem",
  color: "text.primary",
};

// v4's `justify` / `alignContent` / `alignItems` props on the outer
// `<Grid container>`. It is a flex container, so all three rendered; they move
// into `sx` because v9's Grid has no such props (guide §2).
const outerContainerSx: SxProps<Theme> = {
  justifyContent: "center",
  alignContent: "center",
  alignItems: "center",
};

const SignUpStepPurchase = ({
  route,
}: {
  route: "try" | "buy" | "trybuy" | "giftcard" | undefined;
}) => {
  const [buyOpen, setBuyOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>();
  const { runWithLoader, loading } = useContext(OnboardingContext);
  const { validateLogin } = useContext(AccountContext);
  const { logMetricEvent } = useLogger();

  const trialProduct = useMemo(
    () =>
      availableProducts?.find((p) => p.memberStatusId === MEMBER_STATUS_TRIAL),
    [availableProducts],
  );
  const standardProducts = useMemo(
    () =>
      availableProducts?.filter(
        (p) => p.memberStatusId === MEMBER_STATUS_STANDARD,
      ),
    [availableProducts],
  );

  const takeTrial = useCallback(() => {
    if (!trialProduct) return;

    runWithLoader(async () => {
      // #1225: the pixel's StartTrial uses the event ID the server's
      // Facebook listener builds from the trial payment, so Meta counts it once.
      const reply = await createPayment({ productId: trialProduct.id });
      const paymentId = reply?.payment?.id;
      logMetricEvent(
        { type: "free-trial", payload: {} },
        paymentId ? `start_trial:payment:${paymentId}` : undefined,
      );
      await validateLogin();
    });
  }, [runWithLoader, trialProduct, validateLogin, logMetricEvent]);

  const buyNow = useCallback(() => {
    if (!standardProducts) return;
    setBuyOpen(true);
  }, [standardProducts]);

  useEffect(() => {
    runWithLoader(async () => {
      const products = await getProducts();
      setAvailableProducts(products.results);
    }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasTry = !!trialProduct;
  const hasBuy = standardProducts && standardProducts.length > 0;

  return (
    <StepContainer>
      <StepContainer.Body>
        <Grid container sx={outerContainerSx}>
          {!buyOpen && (
            <>
              {/* v4 also had `alignItems="center"` here. This item is NOT a
                  flex/grid container, so the declaration never rendered
                  anything — dropped as a dead rule (guide §2), not forwarded
                  to the DOM. */}
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box sx={infoBoxSx}>
                  <Typography variant="subtitle1" sx={subHeadingSx}>
                    {route === "try" && hasTry
                      ? "You're Ready to Go"
                      : "Welcome to FarmFLiX"}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={bodyTextSx}
                    align="center"
                    style={{
                      fontWeight: 400,
                      color: "#999",
                      lineHeight: "1.1rem",
                      marginBottom: "24px",
                    }}
                  >
                    {route === "try" && hasTry ? (
                      <>
                        You can now login anytime you want. <br />
                        {isBrowser ? "Click" : "Tap"} on &apos;Start Free
                        Trial&apos; to begin.
                      </>
                    ) : (
                      <>
                        Your FarmFLiX Account is all setup and ready to go.{" "}
                        {isBrowser ? "Click" : "Tap"} on &apos;View Membership
                        Plans&apos; to begin.
                      </>
                    )}
                  </Typography>
                </Box>
              </Grid>

              {hasTry && route === "try" && (
                <Grid
                  size={{ xs: 12, sm: 12 }}
                  sx={tryBuyContainerAlignItemsSx}
                >
                  <Button
                    name="try"
                    onClick={takeTrial}
                    size="medium"
                    sx={primaryButtonSx}
                    disabled={loading}
                  >
                    Start Free Trial
                  </Button>
                </Grid>
              )}
              {hasBuy && (route === "buy" || !hasTry) && (
                <Grid size={{ xs: 12, sm: 12 }} sx={tryBuyContainerJustifySx}>
                  <Button
                    name="buy"
                    onClick={buyNow}
                    size="medium"
                    sx={primaryButtonSx}
                  >
                    View Membership Plans
                  </Button>
                </Grid>
              )}
            </>
          )}
          {buyOpen && (
            <BuyNowPurchase
              dark={true}
              title="Choose your Plan"
              showStepper={false}
            />
          )}
        </Grid>
      </StepContainer.Body>
      <StepContainer.Actions />
    </StepContainer>
  );
};

export default SignUpStepPurchase;
